import type { ExternalToast } from "sonner";
import { toast } from "sonner";

type TAlert = "success" | "info" | "warning" | "error";

interface TAlertMessage {
  value: string;
  time: number;
  type: TAlert;
}

let listOfAlertMessages: TAlertMessage[] = [];

/**
 * Transient backend/pooler blips — the Supabase Supavisor pooler tripping its
 * circuit breaker during a reconnect burst, the backend's "briefly busy" 503,
 * or a raw "status code 503". These self-recover in seconds and are already
 * auto-retried, so we stay silent instead of barking a scary error at the user.
 */
const SILENT_TRANSIENT_ERROR =
  /briefly busy|try again in a moment|ECIRCUITBREAKER|too many authentication failures|new connections are temporarily blocked|service unavailable|status code 503/i;

/**
 * this will prevent showing multiple toasts with the same message
 * @param message - Toast message
 * @param type - type of toast
 */
export const showToast = (
  message: string,
  type: TAlert = "info",
  options: ExternalToast = {}
) => {
  if (type) type = type.toLowerCase() as TAlert;

  // Swallow transient connection-blip errors — never surface them to the user.
  if (type === "error" && message && SILENT_TRANSIENT_ERROR.test(message)) {
    return;
  }

  const pushMessageIntoArr = (value: string, type: TAlert) => {
    listOfAlertMessages.push({
      value: value,
      time: Date.now(),
      type,
    });
  };

  const toastListInterval = setInterval(function () {
    const time = Date.now();
    listOfAlertMessages = listOfAlertMessages.filter(function (item) {
      return time < item.time + 5000;
    });
    if (listOfAlertMessages.length === 0) clearInterval(toastListInterval);
  }, 200);

  // check if message exists in array
  if (!listOfAlertMessages.some((msg) => msg.value === message)) {
    pushMessageIntoArr(message, type);
    switch (type) {
      case "success":
        toast.success(message, { ...options });
        break;
      case "info":
        toast.info(message, { ...options });
        break;
      case "warning":
        toast.warning(message, { ...options });
        break;
      case "error":
        toast.error(message, { ...options });
        break;

      default:
        toast.info(message, { ...options });
        break;
    }
  }
};
